/**
 * Schichten und Abwesenheiten in Kalendertermine übersetzen.
 *
 * Eigene Datei, weil beide Sichten — Belegschaft und Planung — dieselbe
 * Übersetzung brauchen und weil sie sich damit ohne DOM prüfen lässt. Die
 * Regeln hier sind fachlich, nicht kosmetisch: Was gestrichelt ist, was rot
 * umrandet wird und was gar nicht erst erscheint, entscheidet, ob jemand einen
 * Entwurf für einen gültigen Plan hält.
 */

import type { Absence, Position, Shift } from './api'
import type { CalendarEvent, CalendarResource } from '../components/Calendar'

/** Farbe einer Position, mit ruhigem Rückfall. */
function positionColor(positions: Position[], positionId: string): string | undefined {
  return positions.find((p) => p.id === positionId)?.color || undefined
}

/**
 * Lesbare Textfarbe zu einer Hintergrundfarbe.
 *
 * Positionsfarben wählt der Betrieb frei — darunter Gelb und helles Grün. Mit
 * fester weißer Schrift wäre die Beschriftung darauf unlesbar. Gerechnet wird
 * die wahrgenommene Helligkeit nach der üblichen Gewichtung; Grün trägt am
 * meisten dazu bei, Blau am wenigsten.
 */
export function readableTextColor(hex: string): string {
  const wert = hex.replace('#', '')
  if (wert.length !== 6) return '#ffffff'
  const r = parseInt(wert.slice(0, 2), 16)
  const g = parseInt(wert.slice(2, 4), 16)
  const b = parseInt(wert.slice(4, 6), 16)
  const helligkeit = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return helligkeit > 0.6 ? '#1a1c1e' : '#ffffff'
}

export type ShiftEventOptions = {
  positions: Position[]
  /** Namen mit in den Titel schreiben — für die Planung sinnvoll, sonst nicht. */
  withNames?: boolean
  /** Schichten ohne ausreichende Besetzung hervorheben. */
  markUnderstaffed?: boolean
}

export function shiftsToEvents(
  shifts: Shift[],
  { positions, withNames = true, markUnderstaffed = true }: ShiftEventOptions,
): CalendarEvent[] {
  return shifts.map((shift) => {
    const farbe = positionColor(positions, shift.position_id)
    const namen = shift.assignments.map((a) => a.full_name)
    const fehlen = shift.min_staff - shift.assignments.length

    const klassen: string[] = []
    if (!shift.is_published) klassen.push('dp-draft')
    if (markUnderstaffed && fehlen > 0) klassen.push('dp-understaffed')

    const kuerzel = positions.find((p) => p.id === shift.position_id)?.abbreviation
    const titel = withNames && namen.length > 0 ? namen.join(', ') : (kuerzel ?? 'Schicht')

    return {
      id: shift.id,
      // ⚠️ `Date`, nicht die ISO-Zeichenkette. Die Bibliothek übernimmt bei
      // einer Zeichenkette den darin stehenden Offset als *Anzeige*-Offset:
      // Aus „08:00Z" wird 08:00 angezeigt, obwohl das 10:00 Berliner Zeit ist.
      // Jede Schicht stünde zwei Stunden zu früh im Plan — plausibel genug,
      // dass es niemandem auffällt. Ein `Date` liest sie dagegen über die
      // lokalen Komponenten aus und zeigt Ortszeit.
      start: new Date(shift.starts_at),
      end: new Date(shift.ends_at),
      // Fehlt jemand, steht das im Titel — nicht nur in der Farbe. Farbe
      // allein ist keine Information für alle.
      title: fehlen > 0 ? `${titel} · ${fehlen} ${fehlen > 1 ? 'fehlen' : 'fehlt'}` : titel,
      resourceIds: [`${shift.location_id}|${shift.position_id}`],
      backgroundColor: farbe,
      textColor: farbe ? readableTextColor(farbe) : undefined,
      // Veröffentlichte Schichten lassen sich ebenso verschieben wie
      // Entwürfe — der Unterschied ist, dass es dann jemand mitbekommt.
      // Gesperrt wird hier nichts, das entscheidet die Rolle.
      extendedProps: {
        klassen,
        isPublished: shift.is_published,
        missing: fehlen,
        namen,
        positionLabel: kuerzel ?? '',
      },
    }
  })
}

/** Zeilen der Ressourcen-Ansicht: je Standort und Position eine. */
export function shiftsToResources(
  shifts: Shift[],
  locations: { id: string; name: string }[],
  positions: Position[],
): CalendarResource[] {
  const gesehen = new Map<string, CalendarResource>()
  for (const shift of shifts) {
    const id = `${shift.location_id}|${shift.position_id}`
    if (gesehen.has(id)) continue
    const ort = locations.find((l) => l.id === shift.location_id)?.name ?? '?'
    const position = positions.find((p) => p.id === shift.position_id)
    gesehen.set(id, {
      id,
      title: `${position?.name ?? '?'} · ${ort}`,
      eventBackgroundColor: position?.color || undefined,
    })
  }
  // Alphabetisch, damit die Zeilen zwischen zwei Wochen nicht springen —
  // nichts ist beim Planen lästiger als ein Raster, das sich neu sortiert.
  return [...gesehen.values()].sort((a, b) => a.title.localeCompare(b.title, 'de'))
}

const ABSENCE_TITEL: Record<string, string> = {
  vacation: 'Urlaub',
  sick: 'Krank',
  other: 'Abwesend',
}

/**
 * Abwesenheiten als ganztägige Hintergrundtermine.
 *
 * Nur Art und Zeitraum — kein Grund, keine Diagnose. Dieselbe Regel wie
 * überall sonst; ein Kalender wird auf Bildschirme geworfen.
 */
export function absencesToEvents(absences: Absence[]): CalendarEvent[] {
  return absences.map((absence) => ({
    id: `absence-${absence.id}`,
    // Der Kalender behandelt das Ende ausschließend, die Abwesenheit
    // einschließend: Ohne den zusätzlichen Tag fehlte der letzte Urlaubstag.
    start: absence.starts_on,
    end: addDay(absence.ends_on),
    title: ABSENCE_TITEL[absence.kind] ?? 'Abwesend',
    editable: false,
    extendedProps: { kind: 'absence', status: absence.status },
  }))
}

/** Ein Tag weiter, rein auf dem Datumsstring — ohne Zeitzonenumweg. */
export function addDay(iso: string): string {
  const teile = iso.split('-').map(Number)
  const [jahr = 1970, monat = 1, tag = 1] = teile
  // `Date.UTC` statt `new Date(...)`: Letzteres rechnet in Ortszeit und
  // liefert bei negativen Zeitzonenversätzen den Vortag zurück.
  const naechster = new Date(Date.UTC(jahr, monat - 1, tag + 1))
  return naechster.toISOString().slice(0, 10)
}


/** Initialen für die Personenkreise auf einer Terminkarte. */
export function initials(name: string): string {
  const teile = name.trim().split(/\s+/).filter(Boolean)
  if (teile.length === 0) return '?'
  if (teile.length === 1) return (teile[0] ?? '?').slice(0, 2).toUpperCase()
  return ((teile[0]?.[0] ?? '') + (teile[teile.length - 1]?.[0] ?? '')).toUpperCase()
}

/**
 * Eine Terminkarte im Plan.
 *
 * Drei Ebenen statt einer Textzeile: Uhrzeit klein und zurückgenommen, die
 * Namen als Initialenkreise, darunter der Klartext. So erkennt man die
 * Besetzung, bevor man liest — und wenn jemand fehlt, steht das als eigene
 * Zeile da und nicht nur als Farbe.
 *
 * Gibt HTML zurück, weil die Bibliothek genau das erwartet. Alles, was aus
 * Nutzereingaben stammt, läuft vorher durch `escapeHtml`.
 */
export function renderShiftEvent(
  info: { timeText: string; event: { extendedProps?: Record<string, unknown> } },
  escape: (t: string) => string,
): string {
  const props = info.event.extendedProps ?? {}
  const namen = (props.namen as string[] | undefined) ?? []
  const fehlen = (props.missing as number | undefined) ?? 0
  const position = (props.positionLabel as string | undefined) ?? ''

  const kreise = namen
    .slice(0, 4)
    .map(
      (name) =>
        `<span class="dp-ev__avatar" title="${escape(name)}">${escape(initials(name))}</span>`,
    )
    .join('')
  const weitere = namen.length > 4 ? `<span class="dp-ev__more">+${namen.length - 4}</span>` : ''

  return [
    '<div class="dp-ev">',
    `<div class="dp-ev__top"><span class="dp-ev__time">${escape(info.timeText)}</span>`,
    position ? `<span class="dp-ev__pos">${escape(position)}</span>` : '',
    '</div>',
    namen.length > 0
      ? `<div class="dp-ev__people">${kreise}${weitere}<span class="dp-ev__names">${escape(
          namen.join(', '),
        )}</span></div>`
      : '',
    fehlen > 0
      ? `<div class="dp-ev__gap">${fehlen} ${fehlen > 1 ? 'fehlen' : 'fehlt'}</div>`
      : '',
    '</div>',
  ].join('')
}
